import React from 'react';

/**
 * Ranked horizontal bar list — the form for "magnitude across a handful of
 * categories" (case counts by issue type, by courier).
 *
 * Design notes, deliberately:
 * - One measure, one series, so a single brand hue fills every bar. Colouring
 *   bars by rank would imply an encoding that isn't there.
 * - Every row is directly labelled with its count and share, so no value is
 *   hidden behind a hover — a tooltip would only repeat what is already on
 *   screen, so hover is a highlight rather than a popup.
 * - Bars carry a 4px rounded data-end anchored to the baseline, and rows are
 *   separated by surface rather than by a grid line.
 * - Beyond `maxItems` categories the tail folds into "Other" rather than
 *   growing an unreadable list.
 */
const BarList = ({ items = [], maxItems = 8, valueSuffix = '' }) => {
  const cleaned = items
    .filter((item) => item && item.value > 0)
    .sort((a, b) => b.value - a.value);

  const visible = cleaned.slice(0, maxItems);
  const tail = cleaned.slice(maxItems);

  if (tail.length) {
    visible.push({
      label: `Other (${tail.length})`,
      value: tail.reduce((sum, item) => sum + item.value, 0)
    });
  }

  const total = cleaned.reduce((sum, item) => sum + item.value, 0);
  const max = visible.length ? Math.max(...visible.map((item) => item.value)) : 0;

  return (
    <ul className="space-y-3.5">
      {visible.map(({ label, value }) => {
        const share = total ? Math.round((value / total) * 100) : 0;
        return (
          <li key={label} className="group">
            <div className="mb-1.5 flex items-baseline justify-between gap-4">
              <span className="min-w-0 truncate text-sm font-medium text-ink-700">{label}</span>
              <span className="shrink-0 text-sm tabular-nums text-ink-500">
                <span className="font-600 text-ink-900">
                  {value.toLocaleString('en-GB')}
                  {valueSuffix}
                </span>
                <span className="ml-2 text-xs text-ink-500">{share}%</span>
              </span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-ink-100">
              <div
                className="h-full origin-left animate-grow rounded-[4px] bg-brand-600 transition-colors duration-150 group-hover:bg-brand-700"
                style={{ width: `${max ? Math.max((value / max) * 100, 2) : 0}%` }}
              />
            </div>
          </li>
        );
      })}
    </ul>
  );
};

export default BarList;
